import React, { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "redux/rootReducer";
//import { loadReport } from "redux/report/reportFunctions";
import { useTranslation } from "react-i18next";
import { Button, withStyles } from "@material-ui/core";
import moment from "moment";
import { VisibleSections } from "types/VisibleSections";
import DownloadIndicator from "./DownloadIndicator";
import { SignalRContext } from "App";
import { b64toBlob, downloadFile } from "utility/files";

const CustomButton = withStyles({
  root: {
    minWidth: 28,
    height: 24,
    padding: 0,
    marginLeft: 6,
  },
})(Button);

const ExportPdf = () => {
  const { t } = useTranslation();

  const user = useSelector((state: RootState) => state.user);

  const visibleSections = useSelector(
    (state: RootState) => state.user.visibleSections?.data as VisibleSections
  );

  const assetClasses = useSelector((state: RootState) =>
    state.assetClassParameters.data?.map((asset) => asset.AssetClass)
  );

  const [reportLoading, setReportLoading] = useState(false);

  SignalRContext.useSignalREffect(
    "ReceiveMessage",
    (report: any) => {
      if (report) {
        // tslint:disable-next-line: no-unsafe-any
        const blob = b64toBlob(report, "application/pdf");
        const filename = "Omaisuusraportti.pdf";

        downloadFile({ filename, blob });
      }
    },
    []
  );

  const startLoadReport = (
    reportControlId: string | undefined,
    startDate: string | undefined,
    endDate: string | undefined,
    assetClasses: string[] | undefined,
    showIndex: boolean,
    showSubIndex: boolean,
    showCommitments: boolean,
    showProfitAndLoss: boolean,
    showDiary: boolean,
    showBalance: boolean
  ) => {
    setReportLoading(true);
    SignalRContext.connection
      ?.invoke(
        "SendMessage",
        reportControlId,
        startDate,
        endDate,
        assetClasses,
        showIndex,
        showSubIndex,
        showCommitments,
        showProfitAndLoss,
        showDiary,
        showBalance
      )
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setReportLoading(false);
      });
  };

  return (
    <div
      className="export-pdf"
      style={{
        position: "absolute",
        top: 0,
        display: "flex",
        flexDirection: "column",
        marginTop: 11,
        cursor: reportLoading ? "default" : "pointer",
      }}
      onClick={
        !reportLoading
          ? () => {
              startLoadReport(
                user.reportControlId,
                moment(user.startDate).format("M/D/YYYY"),
                moment(user.reportDate).format("M/D/YYYY"),
                assetClasses,
                visibleSections.NaytaTuotto,
                visibleSections.NaytaSijoitukset,
                visibleSections.NaytaSitoumukset,
                visibleSections.NaytaVoittoTappio,
                visibleSections.NaytaPaivakirja,
                visibleSections.NaytaSaldo
              );
            }
          : undefined
      }
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          marginBottom: 5,
        }}
      >
        <p
          style={{
            margin: 0,
            color: "#FFFFFF",
            fontFamily: "Lato Light",
            fontSize: "min(max(1.15em, 3vw), 1.3em)",
          }}
        >
          {t("load_pdf_report")}
        </p>
        <CustomButton style={{ cursor: reportLoading ? "default" : "pointer" }}>
          <svg
            className="export-pdf-icon"
            width="28"
            height="24"
            viewBox="0 0 28 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15 7.5L12.5 7.5L12.5 3.5L13.5 3.5L16.5 6.5L16.5 9M11 3.5L5.5 3.5L5.5 11M5.5 16L5.5 20L16.5 20L16.5 18"
              stroke="#FFFFFF"
            />
            <path
              className="extension"
              d="M11.3594 15.1172C11.3594 15.25 11.3112 15.3646 11.2148 15.4609C11.1185 15.5573 11.0039 15.6055 10.8711 15.6055C10.7435 15.6055 10.6315 15.5586 10.5352 15.4648C10.4388 15.3711 10.3906 15.2578 10.3906 15.125C10.3906 14.9792 10.4388 14.862 10.5352 14.7734C10.6315 14.6823 10.7435 14.6367 10.8711 14.6367C10.9987 14.6367 11.112 14.6836 11.2109 14.7773C11.3099 14.8685 11.3594 14.9818 11.3594 15.1172ZM13.7656 15.5703C13.6224 15.5703 13.5026 15.5612 13.4062 15.543C13.3099 15.5247 13.1706 15.4805 12.9883 15.4102V17.3398H12.2773V11.9062H13.6133C14.2122 11.9062 14.6849 12.0625 15.0312 12.375C15.3776 12.6875 15.5508 13.1315 15.5508 13.707C15.5508 14.0534 15.474 14.3698 15.3203 14.6562C15.1693 14.9427 14.9583 15.1667 14.6875 15.3281C14.4193 15.4896 14.112 15.5703 13.7656 15.5703ZM13.6953 14.9492C14.026 14.9492 14.2956 14.8346 14.5039 14.6055C14.7148 14.3763 14.8203 14.0768 14.8203 13.707C14.8203 13.3294 14.7161 13.0391 14.5078 12.8359C14.3021 12.6328 13.9935 12.5312 13.582 12.5312H12.9883V14.7812C13.0742 14.8281 13.1797 14.8685 13.3047 14.9023C13.4297 14.9336 13.5599 14.9492 13.6953 14.9492ZM17.9727 15.5C17.5404 15.5 17.1862 15.4115 16.9102 15.2344C16.6367 15.0547 16.4375 14.8307 16.3125 14.5625C16.1875 14.2917 16.125 14.0117 16.125 13.7227C16.125 13.3294 16.2057 12.9909 16.3672 12.707C16.5312 12.4206 16.7487 12.2044 17.0195 12.0586C17.293 11.9102 17.5859 11.8359 17.8984 11.8359C18.151 11.8359 18.4102 11.8841 18.6758 11.9805V10.043H19.3867V15.5H17.9727ZM18.1172 14.875H18.6758V12.6289C18.4674 12.5169 18.2305 12.4609 17.9648 12.4609C17.6341 12.4609 17.3646 12.5729 17.1562 12.7969C16.9479 13.0208 16.8438 13.332 16.8438 13.7305C16.8438 14.0951 16.9544 14.3776 17.1758 14.5781C17.3971 14.776 17.7109 14.875 18.1172 14.875ZM22.2734 10.0508V10.7148C22.1667 10.6732 22.0286 10.6523 21.8594 10.6523C21.6354 10.6523 21.4701 10.7279 21.3633 10.8789C21.2565 11.0273 21.2031 11.2773 21.2031 11.6289V11.9062H21.8438V12.5469H21.2031V15.5H20.4805V12.5469H19.9766V11.9062H20.4805V11.6211C20.4805 11.1029 20.5951 10.7005 20.8242 10.4141C21.056 10.125 21.3633 9.98047 21.7461 9.98047C21.9284 9.98047 22.1042 10.0039 22.2734 10.0508Z"
              fill="#FFFFFF"
            />
            <path
              className="arrow"
              d="M2 13.5L8 13.5M8 13.5L6.5 12M8 13.5L6.5 15"
              stroke="#FFFFFF"
            />
          </svg>
        </CustomButton>
      </div>
      {reportLoading ? <DownloadIndicator /> : <></>}
    </div>
  );
};

export default React.memo(ExportPdf);
